/**
 * Whether to ask for notification permission, and when (SPEC-016).
 *
 * The browser gives you exactly one chance. `Notification.requestPermission()`
 * can be called once per origin in any meaningful sense — a visitor who answers
 * "block" cannot be asked again by any means the page controls, and on most
 * engines cannot easily undo it either. So the cost of asking at the wrong
 * moment is not an annoyed visitor; it is a permanently closed door.
 *
 * That is the whole reason this module exists as a policy rather than as an
 * `if` inside the hook. The rules below are all one rule: ask only someone who
 * has already shown they want more of this catalogue.
 *
 * Pure — permission and counters arrive as arguments.
 */

/** What the Notification API can tell us, normalised. */
export const NOTIFICATION_STATUS = {
    unsupported: 'unsupported',
    default: 'default',
    granted: 'granted',
    denied: 'denied',
    error: 'error',
};

/** Longer than the install hold: a declined ask is a stronger signal than a dismissed card. */
export const NOTIFICATION_DISMISSAL_HOLD_MS = 60 * 24 * 60 * 60 * 1000;

/**
 * Three visits, or one saved plant.
 *
 * Saving a plant is the stronger signal and the shorter path: someone building
 * a garden has said, in the only way the app offers, that they want to be told
 * when there is more. Three visits is the slower way to say the same thing.
 */
export const VISITS_BEFORE_NOTIFICATION_OFFER = 3;

export function resolveNotificationOffer({
    status = NOTIFICATION_STATUS.unsupported,
    visitCount = 0,
    savedPlantCount = 0,
    dismissedAt = null,
    now = 0,
    holdMs = NOTIFICATION_DISMISSAL_HOLD_MS,
    isInstallOfferShowing = false,
} = {}) {
    // On iOS this is the common case and it is not a failure: Safari exposes no
    // Notification API in a browser tab at all, only in an installed PWA. The
    // honest answer there is to say nothing and let the install offer go first,
    // which is exactly what returning `unsupported` does.
    if (status === NOTIFICATION_STATUS.unsupported || status === NOTIFICATION_STATUS.error) {
        return { canOffer: false, reason: 'unsupported' };
    }

    // Already answered, either way. `granted` needs no card and `denied` cannot
    // be reopened from here — offering would be a button that does nothing.
    if (status !== NOTIFICATION_STATUS.default) {
        return { canOffer: false, reason: status };
    }

    // One offer at a time, and install outranks this one. On iOS it is also a
    // precondition rather than a preference: notifications do not exist until
    // the app is on the home screen.
    if (isInstallOfferShowing) {
        return { canOffer: false, reason: 'install-first' };
    }

    if (savedPlantCount < 1 && visitCount < VISITS_BEFORE_NOTIFICATION_OFFER) {
        return { canOffer: false, reason: 'too-early' };
    }

    if (Number.isFinite(dismissedAt) && dismissedAt > 0 && now - dismissedAt < holdMs) {
        return { canOffer: false, reason: 'dismissed' };
    }

    return { canOffer: true, reason: null };
}
